'use client';

import { useTolgee } from '@tolgee/react';
import { Globe } from 'lucide-react';
import { useEffect, useState } from 'react';

import { selectStyles } from '@/components/styles/select-styles';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notifyLanguageChange } from '@/hooks/useUserLanguage';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'user_language';
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
] as const;

type LanguageCode = (typeof LANGUAGES)[number]['code'];

export function LanguageSelector() {
  const { changeLanguage, getLanguage } = useTolgee();
  const [currentLang, setCurrentLang] = useState<LanguageCode>('en'); // Start with English for SSR
  useEffect(() => {
    const currentTolgeeLanguage = getLanguage();
    setCurrentLang(currentTolgeeLanguage as LanguageCode);
  }, [getLanguage]);

  const handleLanguageChange = async (newLang: LanguageCode) => {
    if (newLang === currentLang) return;

    setCurrentLang(newLang);

    try {
      await changeLanguage(newLang);
      localStorage.setItem(STORAGE_KEY, newLang);
      notifyLanguageChange();

      // No need to reload the page, just let Tolgee handle the language change
      // window.location.reload();
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  return (
    <Select value={currentLang} onValueChange={handleLanguageChange}>
      <SelectTrigger className={cn(selectStyles.darkTrigger)}>
        <div className="flex items-center gap-2">
          <Globe className="size-4" />
          <SelectValue>{LANGUAGES.find((l) => l.code === currentLang)?.label}</SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent className={cn(selectStyles.darkContent)}>
        {LANGUAGES.map((lang) => (
          <SelectItem key={lang.code} value={lang.code} className={cn(selectStyles.darkItem)}>
            {lang.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
